/**
 * PHREATIC API Client
 * Groundwater Monitoring & Prediction Dashboard
 */
import { apiClient } from './client';

// ── Types ────────────────────────────────────────────────────────────────

export interface PhreaticSiteSummary {
  site_id: string;
  site_name: string;
  latitude: number;
  longitude: number;
  well_count: number;
  latest_gauging: string | null;
  avg_depth_to_water_ft: number | null;
  avg_water_elevation_ft: number | null;
  county?: string | null;
  site_area_acres?: number | null;
  aquifer?: string | null;
}

export interface PhreaticSitesResponse {
  sites: PhreaticSiteSummary[];
  total: number;
}

export interface WellMeasurement {
  date: string;
  depth_to_water_ft: number | null;
  water_elevation_ft: number | null;
  product_thickness_ft: number | null;
  is_dry: boolean;
}

export interface WellTimeseries {
  well_id: string;
  well_name: string;
  latitude: number | null;
  longitude: number | null;
  ground_elevation_ft: number | null;
  total_depth_ft: number | null;
  screen_top_ft: number | null;
  screen_bottom_ft: number | null;
  aquifer_name?: string | null;
  measurements: WellMeasurement[];
}

export interface WaterLevelsResponse {
  site_id: string;
  site_name: string;
  wells: WellTimeseries[];
}

export interface WellPoint {
  well_id: string;
  well_name: string;
  latitude: number;
  longitude: number;
  ground_elevation_ft: number | null;
  total_depth_ft: number | null;
  depth_to_water_ft: number | null;
  water_elevation_ft: number | null;
  product_thickness_ft: number | null;
  gauge_date: string | null;
  aquifer_name?: string | null;
  is_dry: boolean;
}

export interface WellMapResponse {
  site_id: string;
  site_name: string;
  center_lat: number;
  center_lon: number;
  wells: WellPoint[];
  gauge_date: string | null;
}

export interface ContourLine {
  elevation_ft: number;
  coordinates: number[][];
}

export interface WaterTableContour {
  site_id: string;
  gauge_date: string;
  contours: ContourLine[];
  flow_direction_deg: number | null;
  flow_magnitude: number | null;
  grid_bounds: {
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
  };
}

export interface PhreaticPageBundle {
  site_id: string;
  site_name: string;
  latitude: number;
  longitude: number;
  site_area_acres: number | null;
  property_bounds: { north: number; south: number; east: number; west: number } | null;
  wells: WellPoint[];
  timeseries: WellTimeseries[];
  available_dates: string[];
  contours: WaterTableContour | null;
  synthetic_clusters?: SyntheticCluster[];
}

/** Pooled single-reading-well cluster — hydrograph + availability only, never
 *  plotted on the 3D surface or gradient map (it's a centroid, not a real point). */
export interface SyntheticCluster {
  lat: number;
  lon: number;
  aquifer: string;
  well_count: number;
  radius_km: number;
  span_years: number;
  ground_ref_ft: number | null;
  current_depth_to_water_ft: number;
  predicted_depth_to_water_ft: Record<string, number>;
  current_water_elevation_ft: number;
  decline_ft_yr: number | null;
  trend_determinate?: boolean;
  fit_r2?: number;
  confidence: string;
  members?: { well_id: string; lat: number; lon: number; date: string; depth_to_water_ft: number; water_elevation_ft: number }[];
}

// ── Prediction Types ──────────────────────────────────────────────────────

export interface PredictionStep {
  target_date: string;
  p10: number;
  p50: number;
  p90: number;
  depth_to_water_ft: number | null;
  cavitation_risk: number;
}

export interface WellPrediction {
  well_id: string;
  well_name: string;
  steps: PredictionStep[];
  trend: 'rising' | 'falling' | 'stable';
  gauging_count: number;
  last_gauging_date: string | null;
}

export interface SitePredictionsResponse {
  site_id: string;
  model_version: string | null;
  generated_at: string | null;
  wells: WellPrediction[];
}

export interface PredictionBundleWell {
  well_id: string;
  well_name: string;
  historical: WellMeasurement[];
  predictions: PredictionStep[];
  trend: 'rising' | 'falling' | 'stable';
}

export interface PredictionBundleResponse {
  site_id: string;
  site_name: string;
  wells: PredictionBundleWell[];
}

// ── API Functions ────────────────────────────────────────────────────────

export const phreaticApi = {
  getSites: async (): Promise<PhreaticSitesResponse> => {
    const { data } = await apiClient.get('/phreatic/sites');
    return data;
  },

  getWaterLevels: async (siteId: string, dateFrom?: string, dateTo?: string): Promise<WaterLevelsResponse> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/water-levels`, { params });
    return data;
  },

  getWellMap: async (siteId: string, gaugeDate?: string): Promise<WellMapResponse> => {
    const params: Record<string, string> = {};
    if (gaugeDate) params.gauge_date = gaugeDate;
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/well-map`, { params });
    return data;
  },

  getWaterTableContours: async (siteId: string, gaugeDate?: string): Promise<WaterTableContour | null> => {
    const params: Record<string, string> = {};
    if (gaugeDate) params.gauge_date = gaugeDate;
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/water-table`, { params });
    return data;
  },

  getAvailableDates: async (siteId: string): Promise<{ dates: string[] }> => {
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/available-dates`);
    return data;
  },

  getPageBundle: async (siteId: string): Promise<PhreaticPageBundle> => {
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/page-bundle`);
    return data;
  },

  // ── Prediction endpoints ────────────────────────────────────────────

  generatePredictions: async (siteId: string, steps: number = 8): Promise<any> => {
    const { data } = await apiClient.post(`/phreatic/sites/${siteId}/generate-predictions`, null, {
      params: { steps },
    });
    return data;
  },

  getPredictions: async (siteId: string): Promise<SitePredictionsResponse> => {
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/predictions`);
    return data;
  },

  getPredictionBundle: async (siteId: string): Promise<PredictionBundleResponse> => {
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/prediction-bundle`);
    return data;
  },

  // ── Calculator endpoints ─────────────────────────────────────────────

  autoAssess: async (params: AutoAssessRequest): Promise<AutoAssessResponse> => {
    const { data } = await apiClient.post('/phreatic/calculator/auto-assess', params);
    return data;
  },

  calcConeOfDepression: async (params: any): Promise<any> => {
    const { data } = await apiClient.post('/phreatic/calculator/cone-of-depression', params);
    return data;
  },

  calcWaterQuality: async (params: any): Promise<any> => {
    const { data } = await apiClient.post('/phreatic/calculator/water-quality', params);
    return data;
  },

  generateReport: async (params: any): Promise<any> => {
    const { data } = await apiClient.post('/phreatic/calculator/generate-report', params);
    return data;
  },

  // ── Reports library (CARI + Reporting tab) ───────────────────────────

  listSiteReports: async (
    siteId: string,
    includeDeleted: boolean = false,
  ): Promise<ReportListResponse> => {
    const { data } = await apiClient.get(`/phreatic/sites/${siteId}/reports`, {
      params: { include_deleted: includeDeleted },
    });
    return data;
  },

  generateSiteReport: async (
    siteId: string,
    body: GenerateReportRequest,
  ): Promise<GenerateReportResponse> => {
    const { data } = await apiClient.post(
      `/phreatic/sites/${siteId}/reports/generate`,
      body,
    );
    return data;
  },

  getReportDownloadUrl: async (
    reportId: string,
    expiresIn: number = 3600,
    disposition: 'inline' | 'attachment' = 'inline',
  ): Promise<DownloadResponse> => {
    const { data } = await apiClient.get(
      `/phreatic/reports/${reportId}/download`,
      { params: { expires_in: expiresIn, disposition } },
    );
    return data;
  },

  deleteReport: async (reportId: string): Promise<{ status: string }> => {
    const { data } = await apiClient.delete(`/phreatic/reports/${reportId}`);
    return data;
  },

  regenerateReport: async (
    reportId: string,
  ): Promise<GenerateReportResponse> => {
    const { data } = await apiClient.post(
      `/phreatic/reports/${reportId}/regenerate`,
    );
    return data;
  },

  getReportProfileCatalog: async (): Promise<ReportProfileCatalog> => {
    const { data } = await apiClient.get('/phreatic/reports/profile-catalog');
    return data;
  },
};

// ── Reports library types ──────────────────────────────────────────────

export interface ReportSummary {
  report_id: string;
  site_id: string | null;
  report_code: string;
  report_title: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  error: string | null;
  size_bytes: number | null;
  page_count: number | null;
  generated_via: 'chat' | 'page';
  generated_by_user_id: string | null;
  parent_report_id: string | null;
  requested_at: string | null;
  completed_at: string | null;
}

export interface ReportListResponse {
  reports: ReportSummary[];
  total: number;
  available_codes: string[];
}

export interface GenerateReportRequest {
  report_code: string;
  params?: Record<string, any>;
  narrative_inputs?: Record<string, any>;
  title_override?: string;
}

export interface GenerateReportResponse {
  report_id: string;
  status: string;
  download_endpoint?: string;
  error?: string;
}

export interface DownloadResponse {
  report_id: string;
  storage_key: string;
  download_url: string;
  expires_in_seconds: number;
}

export interface ReportCatalogEntry {
  report_code: string;
  title: string;
  section_count: number;
  section_titles: string[];
}

export interface ReportProfileCatalog {
  profile: string;
  reports: ReportCatalogEntry[];
}

// ── Calculator Types ───────────────────────────────────────────────────

export interface AutoAssessRequest {
  site_id: string;
  site_area_acres: number;
  demand_type?: 'cattle' | 'poultry' | 'data_center' | 'municipal' | 'custom';
  head_count?: number;
  cooling_mw?: number;
  population?: number;
  custom_gpd?: number;
  pump_rate_gpm_per_well?: number;
  pump_schedule_hrs?: number;
  target_years?: number;
  acceptable_drawdown_pct?: number;
  lstm_steps?: number;
  include_recharge?: boolean;
  client_name?: string;
  aquifer_filter?: string | null;
  gauging_recency_years?: number;
}

export interface SatThicknessProjection {
  date: string;
  p10: number;
  p50: number;
  p90: number;
}

export interface WellSatResult {
  well_id: string;
  current_sat_thickness_ft: number;
  projected_sat_thickness: SatThicknessProjection[];
  depletion_rate_ft_per_year: number;
  years_to_zero_sat: number | null;
  critical: boolean;
}

export interface VolumeResult {
  volume_ft3: number;
  volume_gallons: number;
  volume_acre_feet: number;
  storativity_used: number;
  aquifer: string;
  projected_volume: { date: string; acre_feet_remaining: number }[];
}

export interface DepletionResult {
  daily_demand_gallons: number;
  annual_demand_gallons: number;
  annual_demand_acre_feet: number;
  years_remaining_current_rate: number | null;
  depletion_date_estimate: string | null;
  demand_vs_recharge: {
    demand_af_yr: number;
    estimated_recharge_af_yr: number;
    net_af_yr: number;
    sustainable: boolean;
  } | null;
}

export interface ConeWellResult {
  well_id: string;
  pump_rate_gpm: number;
  drawdown_at_well_ft: number;
  radius_of_influence_ft: number;
}

export interface ConeResult {
  wells: ConeWellResult[];
  contour_grid: {
    bounds: { min_lat: number; max_lat: number; min_lon: number; max_lon: number };
    resolution_ft: number;
    nx: number;
    ny: number;
    drawdown_values: number[][];
    contour_levels_ft: number[];
  } | null;
  simulation_params: {
    time_days: number;
    pump_schedule_hrs: number;
    K_ft_day: number;
    storativity: number;
    transmissivity_ft2_day: number;
  };
}

export interface SustainableResult {
  target_years: number;
  acceptable_drawdown_pct: number;
  max_total_gpm: number;
  max_per_well_gpm: number;
  current_total_gpm: number;
  status: 'sustainable' | 'overdrawn' | 'critical';
  overage_gpm: number;
  recommendation: string;
  scenarios: { years: number; max_gpm: number }[];
  headcount_equivalents: {
    cattle_at_12gal: number;
    current_cattle: number;
    reduce_by: number;
  };
}

export interface RechargeResult {
  annual_precip_history: { year: number; precip_inches: number }[];
  current_year_to_date_inches: number | null;
  average_annual_inches: number | null;
  recharge_rate_inches_yr: number;
  estimated_recharge_af_yr: number;
  aquifer: string;
  recharge_vs_extraction: {
    recharge_af: number;
    extraction_af: number;
    net_af: number;
    sustainable: boolean;
  };
}

export interface AssessmentSummary {
  avg_sat_thickness_ft: number;
  volume_acre_feet: number;
  years_remaining: number | null;
  depletion_date: string | null;
  status: string;
  max_gpm: number;
  critical_wells: number;
  total_wells: number;
  recommendation: string;
}

export interface FloydFactor {
  floyd_applicable?: boolean;
  geology?: {
    aquifer?: string;
    confinement?: string;
    lithology?: string;
    permeability?: string;
    note?: string | null;
  };
  volume_economic_af: number;
  volume_floyd_zone_af: number;
  demand_af_per_year: number;
  demand_years_floyd?: number | null;
  demand_depletion?: Array<Record<string, any>>;
  decline_rates?: {
    v4_inference_ft_yr?: number | null;
    [k: string]: any;
  };
  adjusted_summary?: {
    years_remaining_floyd?: number | null;
    [k: string]: any;
  };
  site_floyd?: {
    comparison?: any;
    [k: string]: any;
  };
  [k: string]: any;
}

export interface AutoAssessResponse {
  site_id: string;
  site_name: string;
  aquifer_detected: string;
  active_aquifer?: string | null;
  available_aquifers?: { name: string; well_count: number }[];
  wells_analyzed: number;
  wells_total_before_filter?: number;
  lstm_predictions_available: number;
  assessment: {
    saturated_thickness: WellSatResult[];
    volume: VolumeResult;
    depletion: DepletionResult;
    cone_of_depression: ConeResult;
    sustainable_rate: SustainableResult;
    summary: AssessmentSummary;
  };
  recharge: RechargeResult | null;
  well_placement: any | null;
  report: any | null;
  floyd_factor?: FloydFactor | null;
}
